import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import "../../../assets/css/AGGrid-custom.css";
import { CustomMultiSelect } from "../../../components/common/inputFields/customMultiSelect";
import { apiGet } from "../../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { ErrorDialog } from "../../../components/common/errorDialog";
import type { Status } from "../../../types/deal";
import type { SponsorTypePipelineSummaryResponse } from "../../../utils/sponsorSummaryHelpers";
import { SummaryDonutChart } from "./SummaryDonutChart";
import { SUMMARY_PANEL_HEIGHT } from "./summaryPanelConstants";

ModuleRegistry.registerModules([AllCommunityModule]);

const PANEL_HEADER_HEIGHT = 72;

const formatCurrency = (value: number) =>
  value.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export function SponsorsTab() {
  const [summary, setSummary] = useState<SponsorTypePipelineSummaryResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const { status } = useReferenceLists();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const statusIds = useMemo(
    () => statusFilter.map((s) => s.id),
    [statusFilter],
  );

  const statusIdsKey = useMemo(
    () => statusIds.slice().sort((a, b) => a - b).join(","),
    [statusIds.join(",")],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    let url = "deals/pipeline/summary?group_by=sponsor_type";
    if (statusIds.length > 0) {
      url += `&${statusIds.map((id) => `status_ids=${id}`).join("&")}`;
    }

    apiGet<SponsorTypePipelineSummaryResponse>(url)
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setSummary(response.data);
          return;
        }
        showErrorFromResponse(response);
        setLoadError("Failed to load sponsor summary.");
      })
      .catch(() => {
        if (cancelled) return;
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch sponsor summary. Please try again.",
        });
        setLoadError("Failed to load sponsor summary.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusIdsKey]);

  const rows = summary?.summaries ?? [];
  const total = summary?.total;

  const tableRows = useMemo(
    () =>
      total
        ? [
            ...rows,
            {
              name: "Total",
              sponsor_count: total.sponsor_count,
              deal_count: total.deal_count,
              loan_amount_gbp: total.loan_amount_gbp,
            },
          ]
        : [],
    [rows, total],
  );

  const columnDefs = useMemo((): ColDef[] => {
    const totalRowStyle = (params: { data?: { name?: string } }) => {
      if (params.data?.name === "Total") {
        return {
          backgroundColor: "#e8eaed",
          color: "#182737",
          borderTop: "3px solid #182737",
        };
      }
      return undefined;
    };

    return [
      {
        field: "name",
        headerName: "Sponsor type",
        flex: 1,
        minWidth: 160,
        cellStyle: totalRowStyle,
      },
      {
        field: "sponsor_count",
        headerName: "Sponsors",
        width: 110,
        cellStyle: (params) => ({
          textAlign: "right" as const,
          ...totalRowStyle(params),
        }),
      },
      {
        field: "deal_count",
        headerName: "Deals",
        width: 90,
        cellStyle: (params) => ({
          textAlign: "right" as const,
          ...totalRowStyle(params),
        }),
      },
      {
        field: "loan_amount_gbp",
        headerName: "Amount (GBP)",
        width: 140,
        valueFormatter: (params) => formatCurrency(params.value ?? 0),
        cellStyle: (params) => ({
          textAlign: "right" as const,
          ...totalRowStyle(params),
        }),
      },
    ];
  }, []);

  const countChartItems = useMemo(
    () =>
      rows.map((row) => ({
        name: row.name,
        value: row.sponsor_count,
      })),
    [rows],
  );

  const amountChartItems = useMemo(
    () =>
      rows.map((row) => ({
        name: row.name,
        value: row.loan_amount_gbp,
      })),
    [rows],
  );

  const panelHeight = Math.max(
    30 + tableRows.length * 30 + PANEL_HEADER_HEIGHT,
    SUMMARY_PANEL_HEIGHT,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
        minWidth: 0,
        pt: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <CustomMultiSelect
          value={statusFilter.map((s) => s.id)}
          onChange={(e) => {
            const selectedIds = e.target.value as (string | number)[];
            setStatusFilter(
              status.filter((opt: Status): opt is Status =>
                selectedIds.includes(opt.id),
              ),
            );
          }}
          options={status}
          placeholder="Select Status"
          labelKey="status"
          minWidth="200px"
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Box sx={{ py: 4, color: "text.secondary" }} className="text-general-page-text">
          {loadError}
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ py: 4, color: "text.secondary" }} className="text-general-page-text">
          No deals match the current filters. Sponsor charts will appear once deals exist
          in the pipeline.
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 2,
            alignItems: "stretch",
            minHeight: panelHeight,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1.1,
              minWidth: { xs: 0, lg: 360 },
              display: "flex",
              minHeight: panelHeight,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                height: "100%",
                minHeight: panelHeight,
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                background:
                  "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  py: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 2,
                  flexShrink: 0,
                }}
              >
                <Typography className="text-general-section-title">
                  Sponsor type breakdown
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: "#182737" }}
                  >
                    {total?.deal_count ?? 0}
                  </Box>{" "}
                  deals
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, px: { xs: 1.5, md: 2 }, py: 1.5 }}>
                <div className="ag-theme-alpine" style={{ width: "100%" }}>
                  <AgGridReact
                    rowData={tableRows}
                    columnDefs={columnDefs}
                    defaultColDef={{
                      flex: 0,
                      resizable: false,
                      filter: false,
                      sortable: false,
                    }}
                    rowHeight={30}
                    headerHeight={30}
                    suppressCellFocus
                    domLayout="autoHeight"
                    suppressHorizontalScroll
                  />
                </div>
              </Box>
            </Paper>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: { xs: 0, lg: 320 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minHeight: panelHeight,
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
              <SummaryDonutChart
                title="Sponsors by type"
                items={countChartItems}
                total={total?.sponsor_count ?? 0}
                formatValue={(v) => String(v)}
                minHeight={Math.floor((panelHeight - 16) / 2)}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
              <SummaryDonutChart
                title="Loan amount by sponsor type (GBP)"
                items={amountChartItems}
                total={total?.loan_amount_gbp ?? 0}
                formatValue={formatCurrency}
                minHeight={Math.floor((panelHeight - 16) / 2)}
              />
            </Box>
          </Box>
        </Box>
      )}

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
